// The credential this browser holds, and how it gets one.
//
// Two builds, one shape. The product build signs in against the app's own
// accounts: an access token for the Authorization header and a refresh token
// to renew it, both minted by `/auth/*`. The header build (VITE_AUTH_MODE=
// header, server started with INBOX_AUTH=header) names a subject instead and
// is for tests and local work. Which one is in force is a build-time setting,
// never a runtime one, so a production bundle cannot be talked into the test
// seam.
//
// The sign-in calls use fetch rather than the generated client: the client
// carries the credential, and these are how a browser gets one.

import { BASE } from '../api/client';

export type AuthMode = 'header' | 'authit';

/** How this build authenticates. `header` is the test seam; see above. */
export const AUTH_MODE: AuthMode = import.meta.env.VITE_AUTH_MODE === 'header' ? 'header' : 'authit';

/** The auth library's floor, restated so a form refuses what the server would. */
export const MIN_PASSWORD = 12;

export interface Tokens {
  /** The bearer, or the test subject in the header build. */
  accessToken: string;
  refreshToken: string;
  /** When the access token expires; empty in the header build. */
  expiresAt: string;
}

const KEY = 'inbox.session';

let tokens: Tokens | null = read();
const listeners = new Set<() => void>();

function read(): Tokens | null {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? 'null');
  } catch {
    return null;
  }
}

export function getTokens(): Tokens | null {
  return tokens;
}

export function setTokens(next: Tokens | null): void {
  tokens = next;
  try {
    if (next) localStorage.setItem(KEY, JSON.stringify(next));
    else localStorage.removeItem(KEY);
  } catch {
    /* a browser with storage switched off still works for this session */
  }
  listeners.forEach((fn) => fn());
}

export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** A refusal from /auth/*, carrying the status so a page can offer the way
 * out that fits — the resend button belongs to a 403, not to a 401. */
export class AuthError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const problem = await res.json().catch(() => ({}));
    const first = problem?.errors?.[0]?.message as string | undefined;
    throw new AuthError(res.status, problem.detail || first || problem.title || `HTTP ${res.status}`);
  }
  const text = res.status === 204 ? '' : await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/** Signs in. In the header build the password is ignored and the address is
 * the subject, which is what lets one screen drive both. */
export async function login(email: string, password = ''): Promise<void> {
  if (AUTH_MODE === 'header') {
    if (!email.trim()) throw new Error('Name a test subject');
    setTokens({ accessToken: email.trim(), refreshToken: '', expiresAt: '' });
    return;
  }
  setTokens(await post<Tokens>('/auth/login', { email: email.trim(), password }));
}

/** Creates an account. The answer is not a session: the address has to be
 * confirmed first, and the page says so. */
export async function register(email: string, password: string): Promise<void> {
  await post<{ email: string }>('/auth/register', { email: email.trim(), password });
}

export async function resendVerification(email: string): Promise<void> {
  await post<void>('/auth/email/resend', { email: email.trim() });
}

export async function verifyEmail(token: string): Promise<void> {
  await post<void>('/auth/email/verify', { token });
}

export async function forgotPassword(email: string): Promise<void> {
  await post<void>('/auth/password/forgot', { email: email.trim() });
}

/** Whether a mailed link can still set a password, asked before somebody
 * types one into a page that was going to refuse it. Does not spend it. */
export async function checkPasswordLink(token: string): Promise<void> {
  await post<void>('/auth/password/check', { token });
}

export async function setPassword(token: string, password: string): Promise<void> {
  await post<void>('/auth/password/set', { token, password });
}

export async function logout(): Promise<void> {
  const t = tokens;
  setTokens(null);
  // Best effort: the session is over here whatever the server says, and a
  // refresh token that outlives the browser is what the endpoint is for.
  if (AUTH_MODE === 'authit' && t?.refreshToken) {
    await post<void>('/auth/logout', { refreshToken: t.refreshToken }).catch(() => {});
  }
}

let inflight: Promise<boolean> | null = null;

/**
 * Trades the refresh token for a fresh pair. The refresh token is single use,
 * so two requests racing to renew would sign the second one out: they share
 * one attempt instead. The header build has no session to renew.
 */
export function refresh(): Promise<boolean> {
  if (inflight) return inflight;
  const t = tokens;
  if (!t || AUTH_MODE === 'header' || !t.refreshToken) return Promise.resolve(false);
  inflight = post<Tokens>('/auth/refresh', { refreshToken: t.refreshToken })
    .then((pair) => {
      setTokens(pair);
      return true;
    })
    .catch(() => {
      setTokens(null);
      return false;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/** Renews the access token when it is about to expire, so a request is not
 * sent with a token the server will refuse on arrival. */
export async function freshAccessToken(): Promise<string | null> {
  const t = tokens;
  if (!t) return null;
  if (AUTH_MODE === 'authit' && t.expiresAt && Date.parse(t.expiresAt) - Date.now() < 30_000) {
    if (!(await refresh())) return null;
  }
  return tokens?.accessToken ?? null;
}
